import { toNumber, toUtcDate } from '@rahtab/system/lib/tools/string';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { deserialize, serialize } from 'class-transformer';
import { OfferTemplateInfo } from 'hooks/useMerchandises';
import { RootState } from 'rahtab-redux';
import type { GetTemplateOfferListResult, OfferInfo } from '../../hooks/useListings';
import type { Rateable, SliceState } from '../../interfaces';

const initialState: ListingSliceState = {
  loading: 'idle',
  listings: {},
};

const ListingsSlice = createSlice({
  name: 'listings',
  initialState,
  reducers: {
    setListingsLoading(state, { payload }: PayloadAction<ListingSliceState['loading']>) {
      state.loading = payload;
    },
    addListing(
      state,
      {
        payload,
      }: PayloadAction<
        OfferInfo &
          Pick<GetTemplateOfferListResult, 'maxOrderingValue' | 'sumOfferMeasureName'> &
          Pick<OfferTemplateInfo, 'TemplateCode'> & { termName?: string }
      >,
    ) {
      const {
        OfferCode: id,
        RemainingValue: remainingQuantity,
        SupplierIdentification: sellerId,
        SupplierName: sellerName,
        SupplierImage: selllerImage,
        CommentCount: sellerRatingCount,
        DeliveryLocation: sellerCity,
        MeasureName: unitText,
        MeasureCode: unit,
        PricePerUnit: price,
        DeliveryDate: deliveryDate,
        maxOrderingValue,
        sumOfferMeasureName: merchandiseUnitText,
        TemplateCode: merchandiseId,
        ClientAvailableAmount: viewerOrderLimit,
        termName,
        HasPriority,
      } = payload;
      const listing: Listing = {
        id,
        viewerOrderLimit: toNumber(viewerOrderLimit),
        remainingQuantity: toNumber(remainingQuantity),
        merchandiseUnitText,
        merchandiseId,
        seller: {
          id: sellerId,
          name: sellerName,
          imageUrl: selllerImage,
          ratingCount: toNumber(sellerRatingCount),
          rating: 0,
          city: sellerCity,
        },
        unitText,
        unit,
        price: toNumber(price),
        deliveryDate: <Date>(<unknown>serialize(toUtcDate(deliveryDate))),
        termName,
        active: HasPriority ?? true,
      };
      state.listings[id] = listing;
    },
    setViewerOrderLimit(state, { payload }: PayloadAction<{ id: string; value: number }>) {
      state.listings[payload.id].viewerOrderLimit = payload.value;
    },
  },
});

const { actions, reducer } = ListingsSlice;

export const { addListing, setListingsLoading, setViewerOrderLimit } = actions;

export default reducer;

export function listingSelector(ids: string): (state: RootState) => Listing;
export function listingSelector(ids: string[]): (state: RootState) => Array<Listing>;
export function listingSelector(ids: string | string[]) {
  return (state: RootState) => {
    const { listings } = state;
    if (typeof ids === 'string') return getListing(ids);
    return ids.map((id) => getListing(id));
    function getListing(id: string): Listing {
      return {
        ...listings.listings[id],
        deliveryDate: deserialize(Date, <string>(<unknown>listings.listings[id].deliveryDate)),
      };
    }
  };
}
interface Seller extends Rateable {
  id: string;
  name: string;
  imageUrl: string;
  city: string;
}
interface Listing {
  id: string;
  merchandiseId: string;
  viewerOrderLimit: number;
  remainingQuantity: number;
  seller: Seller;
  unitText: string;
  unit: string;
  price: number;
  deliveryDate: Date;
  merchandiseUnitText: string;
  termName?: string;
  active: boolean;
}

type ListingSliceState = SliceState<'listings', Listing>;
